import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { colors, fonts, theme } from './tokens';

// Industry component layer as styled-components primitives.
// Every shape is square (radius 0); fills are reserved for primary buttons, tags,
// active chips and the Contatti field. Everything else is a hairline line drawing.

/* — layout — */
export const Container = styled.div`
  max-width: ${theme.containerMax};
  margin: 0 auto;
  padding-inline: ${theme.containerPad};
`;

/* — blueprint frame: hairline border + four "+" registration marks — */
const Corner = styled.i`
  position: absolute;
  width: 11px;
  height: 11px;
  color: rgba(29, 31, 32, 0.55);
  pointer-events: none;
  ${({ $y }) => ($y === 't' ? 'top: -6px;' : 'bottom: -6px;')}
  ${({ $x }) => ($x === 'l' ? 'left: -6px;' : 'right: -6px;')}
  &::before, &::after { content: ''; position: absolute; background: currentColor; }
  &::before { left: 5px; top: 0; width: 1px; height: 100%; }
  &::after { top: 5px; left: 0; width: 100%; height: 1px; }
`;

export const Corners = () => (
  <>
    <Corner $y="t" $x="l" aria-hidden="true" />
    <Corner $y="t" $x="r" aria-hidden="true" />
    <Corner $y="b" $x="l" aria-hidden="true" />
    <Corner $y="b" $x="r" aria-hidden="true" />
  </>
);

export const blueprintFrame = css`
  position: relative;
  border: 1px solid ${colors.divider};
  border-radius: 0;
`;

const BlueprintBox = styled.div`
  ${blueprintFrame}
`;

// <Blueprint as="article" …> renders its children inside a framed box with corner marks.
export const Blueprint = forwardRef(({ children, ...rest }, ref) => (
  <BlueprintBox ref={ref} {...rest}>
    <Corners />
    {children}
  </BlueprintBox>
));
Blueprint.displayName = 'Blueprint';

/* — photo frame: fixed aspect box, photo covers it in its original colours — */
// The <img> is absolutely positioned so its intrinsic size never feeds back into the box
// (with aspect-ratio + height:100% WebKit could keep re-growing the layout).
export const PhotoFrame = styled.div`
  position: relative;
  min-width: 0;
  aspect-ratio: ${({ $ratio = '4 / 3' }) => $ratio};
  overflow: ${({ $framed }) => ($framed ? 'visible' : 'hidden')};
  background: ${colors.neutral200};
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

/* — buttons — */
const variants = {
  primary: css`
    background: ${colors.accent};
    border-color: ${colors.accent};
    color: ${colors.ink};
    &:hover { background: ${colors.accent600}; color: ${colors.ink}; }
    &:active { background: ${colors.accent700}; }
  `,
  secondary: css`
    border-color: ${colors.divider};
    &:hover { background: rgba(29, 31, 32, 0.07); }
    &:active { background: rgba(29, 31, 32, 0.14); }
  `,
  ghost: css`
    color: ${colors.accent800};
    border-color: transparent;
    padding-inline: 3.4px;
    &:hover { background: rgba(238, 185, 22, 0.1); }
    &:active { background: rgba(238, 185, 22, 0.18); }
  `,
  // Ink-filled with yellow text (Contatti / CTA strip).
  ink: css`
    background: ${colors.ink};
    border-color: ${colors.ink};
    color: ${colors.accent};
    &:hover { background: #000; color: ${colors.accent}; }
  `,
  // Outlined ink on the yellow field.
  outline: css`
    border-color: ${colors.ink};
    &:hover { background: rgba(29, 31, 32, 0.1); color: ${colors.ink}; }
  `,
};

export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  text-decoration: none;
  font-family: ${fonts.heading};
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  color: ${colors.ink};
  background: transparent;
  border: 1px solid ${colors.divider};
  border-radius: 0;
  padding: 6.8px 12.24px;
  white-space: nowrap;
  transition: background-color 0.15s, border-color 0.15s;
  svg { display: block; flex: none; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  ${({ $variant = 'secondary' }) => variants[$variant]}
  ${({ $icon }) => $icon && css`width: 44px; height: 44px; padding: 0;`}
  ${({ $blueprint }) => $blueprint && css`position: relative;`}
`;

// Primary blueprint CTA (button + corner marks), e.g. "Scopri la flotta".
export const BlueprintButton = forwardRef(({ children, ...rest }, ref) => (
  <Button ref={ref} $variant="primary" $blueprint {...rest}>
    <Corners />
    {children}
  </Button>
));
BlueprintButton.displayName = 'BlueprintButton';

/* — tag — */
export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 0;
  background: ${colors.accent};
  color: ${colors.ink};
`;

/* — input — */
export const Input = styled.input`
  width: 100%;
  min-height: 36px;
  padding: 6px 10px;
  font: inherit;
  font-size: 14px;
  color: ${colors.ink};
  caret-color: ${colors.accent};
  background: ${colors.surface};
  border: 1px solid ${colors.divider};
  border-radius: 0;
  -webkit-appearance: none;
  appearance: none;
  &:hover { border-color: rgba(29, 31, 32, 0.45); }
  &:focus-visible { border-color: ${colors.accent}; outline-offset: 0; }
  &::placeholder { color: ${colors.neutral600}; }
`;

/* — section label (micro kicker) — */
export const SectionLabel = styled.span`
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.neutral700};
`;

export const MicroLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.neutral700};
`;

/* — hairline grid: 1px gaps drawn by the divider background — */
export const HairlineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $fit }) => ($fit ? 'auto-fit' : 'auto-fill')}, minmax(min(100%, ${({ $min = 280 }) => $min}px), 1fr));
  gap: 1px;
  background: ${colors.divider};
  border: 1px solid ${colors.divider};
  > * { background: ${colors.bg}; }
`;

/* — display headings — */
export const DisplayH2 = styled.h2`
  margin: 0;
  font-size: clamp(40px, 6vw, 88px);
  line-height: 0.9;
  text-transform: uppercase;
  letter-spacing: -0.02em;
`;

// Small label/value spec rows used on cards.
export const SpecRows = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${colors.divider};
`;

export const SpecRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid ${colors.divider};
  font-size: 13px;
  span:first-child { color: ${colors.neutral700}; }
  span:last-child { font-weight: 700; text-align: right; }
`;
